import type { VisibilityState } from '@tanstack/react-table';
import type { BillingBudgetField, BillingColumnDef } from '../types/billing';
import { BILLING_TABLE_COLUMNS } from '../types/billing';

export type BillingColumnGroupId =
  | 'account'
  | 'ownership'
  | 'currency'
  | 'annual_primary'
  | 'annual_secondary'
  | 'q1_months'
  | 'q2_months'
  | 'q3_months'
  | 'q4_months';

export interface BillingColumnGroup {
  id: BillingColumnGroupId;
  label: string;
  columnIds: BillingBudgetField[];
  defaultVisible: boolean;
}

const MONTH_PREFIXES = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const;

export type MonthPrefix = (typeof MONTH_PREFIXES)[number];

export const MONTH_DISPLAY_LABELS: Record<MonthPrefix, string> = {
  jan: 'Jan',
  feb: 'Feb',
  mar: 'Mar',
  apr: 'Apr',
  may: 'May',
  jun: 'Jun',
  jul: 'Jul',
  aug: 'Aug',
  sep: 'Sep',
  oct: 'Oct',
  nov: 'Nov',
  dec: 'Dec',
};

export function monthFieldsForPrefix(prefix: MonthPrefix): BillingBudgetField[] {
  return [`${prefix}_spend`, `${prefix}_budget`] as BillingBudgetField[];
}

export function getMonthPrefixFromField(field: BillingBudgetField): MonthPrefix | null {
  const match = field.match(
    /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)_(spend|budget)$/,
  );
  return match ? (match[1] as MonthPrefix) : null;
}

export function isMonthFieldVisible(
  prefix: MonthPrefix,
  columnVisibility: VisibilityState,
): boolean {
  const spend = `${prefix}_spend` as BillingBudgetField;
  const budget = `${prefix}_budget` as BillingBudgetField;
  return columnVisibility[spend] !== false || columnVisibility[budget] !== false;
}

export function setMonthVisibility(
  prefix: MonthPrefix,
  visible: boolean,
  prev: VisibilityState,
): VisibilityState {
  const next = { ...prev };
  const spend = `${prefix}_spend`;
  const budget = `${prefix}_budget`;
  if (visible) {
    delete next[spend];
    delete next[budget];
  } else {
    next[spend] = false;
    next[budget] = false;
  }
  return next;
}

export type BillingDisplayColumn =
  | { kind: 'single'; col: BillingColumnDef }
  | {
      kind: 'month';
      prefix: MonthPrefix;
      label: string;
      spendField: BillingBudgetField;
      budgetField: BillingBudgetField;
    };

export function allMonthlyFields(): BillingBudgetField[] {
  return MONTH_PREFIXES.flatMap((p) => monthFieldsForPrefix(p));
}

/** Calendar quarter 1–4 for a month index 0–11. */
export function quarterForMonth(monthIndex: number): 1 | 2 | 3 | 4 {
  return (Math.floor(monthIndex / 3) + 1) as 1 | 2 | 3 | 4;
}

export function getCurrentQuarterMonthPrefixes(referenceDate = new Date()): MonthPrefix[] {
  const q = quarterForMonth(referenceDate.getMonth());
  const start = (q - 1) * 3;
  return [MONTH_PREFIXES[start], MONTH_PREFIXES[start + 1], MONTH_PREFIXES[start + 2]];
}

export function getCurrentQuarterMonthFields(referenceDate = new Date()): BillingBudgetField[] {
  return getCurrentQuarterMonthPrefixes(referenceDate).flatMap((p) => monthFieldsForPrefix(p));
}

export const BILLING_COLUMN_LABELS: Partial<Record<BillingBudgetField, string>> = {
  account_id: 'Billing Account',
  account_name: 'Account Name',
  business_unit: 'BU',
  sub_bu: 'Sub-BU',
  cloud_type: 'Cloud Type',
  bu_head_email: 'BU Head',
  sbu_head_email: 'SBU Head',
  owner_email: 'Owner',
  currency: 'Currency',
  conversion_rate: 'FX Rate',
  annual_fixed_budget: 'Annual Budget',
  annual_budget_revised_month: 'Revised Month',
  annual_remaining: 'Annual Remaining',
  cumulative_overspend: 'Cumulative Overspend',
  saved: 'Saved',
  ytd_spend: 'YTD Spend',
  ytd_percentage: 'YTD %',
  percentage_spent_of_total: '% of Total',
  status: 'Status',
  is_flagged: 'Flagged',
};

export function getBillingColumnLabel(field: BillingBudgetField): string {
  if (BILLING_COLUMN_LABELS[field]) {
    return BILLING_COLUMN_LABELS[field]!;
  }
  const col = BILLING_TABLE_COLUMNS.find((c) => c.id === field);
  return col?.label ?? field;
}

function quarterGroupColumnIds(quarter: 1 | 2 | 3 | 4): BillingBudgetField[] {
  const start = (quarter - 1) * 3;
  return [
    ...monthFieldsForPrefix(MONTH_PREFIXES[start]),
    ...monthFieldsForPrefix(MONTH_PREFIXES[start + 1]),
    ...monthFieldsForPrefix(MONTH_PREFIXES[start + 2]),
  ];
}

export function buildBillingColumnGroups(referenceDate = new Date()): BillingColumnGroup[] {
  const currentQ = quarterForMonth(referenceDate.getMonth());
  const quarterVisible = (q: 1 | 2 | 3 | 4) => q === currentQ;

  return [
    {
      id: 'account',
      label: 'Account',
      columnIds: ['account_id', 'account_name', 'business_unit', 'sub_bu', 'cloud_type'],
      defaultVisible: true,
    },
    {
      id: 'ownership',
      label: 'Ownership',
      columnIds: ['bu_head_email', 'sbu_head_email', 'owner_email'],
      defaultVisible: false,
    },
    {
      id: 'currency',
      label: 'Currency',
      columnIds: ['currency', 'conversion_rate'],
      defaultVisible: true,
    },
    {
      id: 'annual_primary',
      label: 'Annual Summary',
      columnIds: [
        'annual_fixed_budget',
        'ytd_spend',
        'ytd_percentage',
        'status',
        'is_flagged',
      ],
      defaultVisible: true,
    },
    {
      id: 'annual_secondary',
      label: 'Annual Details',
      columnIds: [
        'annual_budget_revised_month',
        'annual_remaining',
        'cumulative_overspend',
        'saved',
        'percentage_spent_of_total',
      ],
      defaultVisible: false,
    },
    {
      id: 'q1_months',
      label: 'Q1 (Jan–Mar)',
      columnIds: quarterGroupColumnIds(1),
      defaultVisible: quarterVisible(1),
    },
    {
      id: 'q2_months',
      label: 'Q2 (Apr–Jun)',
      columnIds: quarterGroupColumnIds(2),
      defaultVisible: quarterVisible(2),
    },
    {
      id: 'q3_months',
      label: 'Q3 (Jul–Sep)',
      columnIds: quarterGroupColumnIds(3),
      defaultVisible: quarterVisible(3),
    },
    {
      id: 'q4_months',
      label: 'Q4 (Oct–Dec)',
      columnIds: quarterGroupColumnIds(4),
      defaultVisible: quarterVisible(4),
    },
  ];
}

/** Hidden by default even though their group is otherwise shown. */
const DEFAULT_HIDDEN_FIELDS: BillingBudgetField[] = [
  'business_unit',
  'sub_bu',
  'cloud_type',
  'conversion_rate',
];

/** Shown by default even though their group is otherwise hidden. */
const DEFAULT_VISIBLE_FIELDS: BillingBudgetField[] = [];

/** Apply the per-column overrides on top of the group-based defaults. */
function applyDefaultVisibilityOverrides(visible: Set<BillingBudgetField>): void {
  DEFAULT_HIDDEN_FIELDS.forEach((id) => visible.delete(id));
  DEFAULT_VISIBLE_FIELDS.forEach((id) => visible.add(id));
}

/** TanStack visibility: false = hidden. Essentials preset. */
export function buildDefaultColumnVisibility(referenceDate = new Date()): VisibilityState {
  const groups = buildBillingColumnGroups(referenceDate);
  const visible = new Set<BillingBudgetField>();
  for (const g of groups) {
    if (g.defaultVisible) {
      g.columnIds.forEach((id) => visible.add(id));
    }
  }
  applyDefaultVisibilityOverrides(visible);
  const visibility: VisibilityState = {};
  for (const col of BILLING_TABLE_COLUMNS) {
    if (!visible.has(col.id)) {
      visibility[col.id] = false;
    }
  }
  return visibility;
}

export type BillingColumnPresetId =
  | 'essentials'
  | 'full_year'
  | 'all_columns'
  | 'ownership_details';

export function columnVisibilityForPreset(
  preset: BillingColumnPresetId,
  referenceDate = new Date(),
): VisibilityState {
  const groups = buildBillingColumnGroups(referenceDate);
  const visible = new Set<BillingBudgetField>();

  const alwaysAccount = groups.find((g) => g.id === 'account')!.columnIds;
  const alwaysCurrency = groups.find((g) => g.id === 'currency')!.columnIds;
  const annualPrimary = groups.find((g) => g.id === 'annual_primary')!.columnIds;
  const annualSecondary = groups.find((g) => g.id === 'annual_secondary')!.columnIds;
  const ownership = groups.find((g) => g.id === 'ownership')!.columnIds;
  const monthGroups = groups.filter((g) => g.id.startsWith('q'));

  if (preset === 'ownership_details') {
    const base = columnVisibilityForPreset('essentials', referenceDate);
    ownership.forEach((id) => {
      delete base[id];
    });
    annualSecondary.forEach((id) => {
      delete base[id];
    });
    return base;
  }

  switch (preset) {
    case 'essentials':
      [...alwaysAccount, ...alwaysCurrency, ...annualPrimary].forEach((id) => visible.add(id));
      getCurrentQuarterMonthFields(referenceDate).forEach((id) => visible.add(id));
      applyDefaultVisibilityOverrides(visible);
      break;
    case 'full_year':
      [...alwaysAccount, ...alwaysCurrency, ...annualPrimary].forEach((id) => visible.add(id));
      monthGroups.flatMap((g) => g.columnIds).forEach((id) => visible.add(id));
      break;
    case 'all_columns':
      BILLING_TABLE_COLUMNS.forEach((c) => visible.add(c.id));
      break;
    default:
      break;
  }

  const visibility: VisibilityState = {};
  for (const col of BILLING_TABLE_COLUMNS) {
    if (!visible.has(col.id)) {
      visibility[col.id] = false;
    }
  }
  return visibility;
}

export const BILLING_FILTERABLE_COLUMNS: BillingBudgetField[] = [
  'business_unit',
  'sub_bu',
  'cloud_type',
  'status',
];

export const BILLING_SORTABLE_COLUMNS: BillingBudgetField[] = [
  'account_id',
  'account_name',
  'business_unit',
  'sub_bu',
  'cloud_type',
  'annual_fixed_budget',
  'cumulative_overspend',
  'ytd_spend',
  'ytd_percentage',
  'status',
  'currency',
  ...allMonthlyFields(),
];

export const BILLING_SEARCH_FIELDS: BillingBudgetField[] = [
  'account_id',
  'account_name',
  'business_unit',
  'sub_bu',
  'owner_email',
  'currency',
];

export const BILLING_STICKY_COLUMN_IDS: BillingBudgetField[] = [
  'account_id',
  'account_name',
  'business_unit',
];

export function getVisibleBillingColumns(
  columnVisibility: VisibilityState,
): BillingColumnDef[] {
  return BILLING_TABLE_COLUMNS.filter((col) => columnVisibility[col.id] !== false).map(
    (col) => ({
      ...col,
      label: getBillingColumnLabel(col.id),
      sticky: BILLING_STICKY_COLUMN_IDS.includes(col.id),
    }),
  );
}

/** Table layout: non-month columns + month groups (spend/budget as sub-columns). */
export function buildBillingDisplayColumns(
  columnVisibility: VisibilityState,
): BillingDisplayColumn[] {
  const result: BillingDisplayColumn[] = [];
  const seenMonths = new Set<MonthPrefix>();

  for (const col of BILLING_TABLE_COLUMNS) {
    const prefix = getMonthPrefixFromField(col.id);
    if (prefix) {
      if (seenMonths.has(prefix)) continue;
      if (!isMonthFieldVisible(prefix, columnVisibility)) continue;
      seenMonths.add(prefix);
      result.push({
        kind: 'month',
        prefix,
        label: MONTH_DISPLAY_LABELS[prefix],
        spendField: `${prefix}_spend` as BillingBudgetField,
        budgetField: `${prefix}_budget` as BillingBudgetField,
      });
      continue;
    }
    if (columnVisibility[col.id] === false) continue;
    result.push({
      kind: 'single',
      col: {
        ...col,
        label: getBillingColumnLabel(col.id),
        sticky: BILLING_STICKY_COLUMN_IDS.includes(col.id),
      },
    });
  }
  return result;
}

export function countDisplayLeafColumns(columnVisibility: VisibilityState): number {
  return buildBillingDisplayColumns(columnVisibility).reduce(
    (n, d) => n + (d.kind === 'month' ? 2 : 1),
    0,
  );
}

/** @deprecated Use countDisplayLeafColumns for UI column count. */
export function countVisibleColumns(columnVisibility: VisibilityState): number {
  return countDisplayLeafColumns(columnVisibility);
}
